import uniqBy from "lodash.uniqby";
import React from "react";
import { SurveyAnswers } from "../../common/hooks/api/useSurvey";
import { OrganizationDataRecipientDto } from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { SurveyQuestionProps } from "../components/get-compliant/survey/SurveyQuestion";
import {
  findAlwaysSellingVendors,
  findGoogleAnalytics,
  findShopify,
  findShopifyAudiences,
} from "../util/vendors";
import { onlyGaSelectedForSelling } from "./getAnswer";
import { SurveyDefinition } from "./survey";
import { alphabetically, vendorToCheckboxOption } from "./util";

const sellingInstructions = (): SurveyQuestionProps => {
  return {
    type: "acknowledgement",
    question: 'What is data "selling"?',
    slug: "selling-definition-acknowledgement",
    text: (
      <>
        <p>
          Consumers also have the right to opt-out of data selling. “Selling" is broader than you
          might think and involves exchanging personal information for money or anything else of
          value. It may include disclosing personal information to a vendor and receiving a free
          service in return.
        </p>
        <p>
          “Selling” excludes situations where a consumer intentionally uses your business, platform,
          or service to send their data to a specific third party.
        </p>
      </>
    ),
  };
};

export const sellingDisclosureQuestion = (
  answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: "disclosure-for-selling",
    question:
      "Does your business disclose — or give third parties access to — personal data about its consumers and receive free or discounted services, products, access to data, or anything else of value in return?",
    helpText: (
      <>
        This is considered “selling” under the broad definition in some state privacy laws and it
        requires that you give consumers the ability to opt-out. “Yes” is auto-selected here if you
        use Google Analytics as a free service (and have not turned on RDP) or if you use certain
        email scraping data brokers as vendors. Select “yes” here if you disclose consumer data to
        marketing cooperatives and receive access to product, services, or special pricing in
        return, or if you disclose data in similar scenarios.
      </>
    ),
    noDisabled: overrideDisclosureForSellingQuestion(answers, vendors),
    noDisabledTooltip: "‘Yes’ is selected here based on your answers to the previous questions.",
  };
};

export const googleAnalyticsQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: "google-analytics-data-sharing",
  question: "Has your business turned off (or will it turn off) data sharing in Google Analytics?",
  helpText: (
    <>
      By default, Google accesses consumer data (for example, data collected from website visitors)
      in order to improve its products and services. If your business turns off data sharing, Google
      will no longer access that data. If data sharing is on, your business is “selling” data and
      will need to include an opt-out link on its website.{" "}
      <a
        href="https://support.google.com/analytics/answer/1011397?hl=en&ref_topic=2919631"
        target="_blank"
        rel="noreferrer noopener"
      >
        How to turn off data sharing in Google Analytics
      </a>
      .
    </>
  ),
});

export const SHOPIFY_SNI_SLUG = "shopify-sni";
export const shopifySNIQuestion = (): SurveyQuestionProps => ({
  type: "boolean",
  slug: SHOPIFY_SNI_SLUG,
  question: "Has your business turned off Shopify Network Intelligence (SNI)?",
  plaintextQuestion: "Has your business turned off Shopify Network Intelligence (SNI)?",
  disabledAnswer: "false",
  helpText: (
    <>
      By default, Shopify automatically opts stores into SNI, which gives them access to Shopify
      Audiences and other features. You can{" "}
      <a
        href="https://help.shopify.com/en/manual/privacy-and-security/privacy/customer-privacy-settings/privacy-settings#network-intelligence"
        target="_blank"
        rel="noreferrer noopener"
      >
        read about SNI here
      </a>
      . When SNI is enabled, your business is "selling" and "sharing" data with Shopify, which
      triggers opt-out requirements. If you answer "no" (meaning you left the default settings on),
      we'll automatically incorporate those requirements into your Data Map and Privacy Center.
    </>
  ),
});

export const sellingVendorsQuestion = (
  vendors: OrganizationDataRecipientDto[],
): SurveyQuestionProps => {
  const sellingVendors = vendors
    .filter((v) => v.collectionContext.includes("CONSUMER"))
    .filter((v) => v.isPotentialSellingVendor);
  const sellingVendorOptions = sellingVendors.sort(alphabetically).map((v) => {
    if (v.isAutomaticSellingVendor || v.isAlwaysDataSellingVendor) {
      let tooltip = v.isAlwaysDataSellingVendor
        ? // vendors for which we know that data is always considered "sold" to, by the nature of their services
          "This vendor’s privacy documentation states that it uses data sold to it by its users."
        : // vendors for which we have deduced that data is being "sold" to based on user-specified usage patterns of that vendor’s services
          "You have indicated you are using features which require this data recipient to be considered “selling” data.";
      if (v.vendorKey == "shopify-audiences") {
        tooltip =
          "Shopify Audiences shares your customers' data with third parties for marketing purposes, which is considered “selling” in some regions.";
      }
      return vendorToCheckboxOption(v, {
        value: true,
        disabled: true,
        title: tooltip,
      });
    }
    return vendorToCheckboxOption(v);
  });

  return {
    type: "checkboxes",
    slug: "vendor-selling-selection",
    question: `Which third parties does your business "sell" to?`,
    helpText: (
      <>
        Some of your vendors are excluded from this list because, based on our research, they have
        publicly stated that they do not use data in a manner that requires consumer opt-out rights.
      </>
    ),
    emptyText: "There are no vendors available.",
    options: uniqBy(sellingVendorOptions, (v) => v.key),
  };
};

export const SELLING_UPLOAD_DATA_SLUG = "selling-upload-data";
export const sellingUploadDataQuestion = (): SurveyQuestionProps => {
  return {
    type: "boolean",
    slug: SELLING_UPLOAD_DATA_SLUG,
    question:
      "Does your business manually upload, transmit, or grant access to consumer data to any of the third parties it “sells” to?",
    helpText: (
      <>
        For example, if your business sends customer lists to other organizations, such as marketing
        cooperatives.
      </>
    ),
  };
};

export const sellingUploadVendorsSelectionQuestion = (
  vendors: OrganizationDataRecipientDto[],
): SurveyQuestionProps => {
  const uploadVendors = vendors
    .filter((v) => v.collectionContext.includes("CONSUMER"))
    .filter((v) => v.isPotentialUploadVendor || v.isAlwaysDataSellingVendor);
  const uploadVendorOptionsOptions = uploadVendors.sort(alphabetically).map((v) => {
    if (v.isAlwaysUploadVendor || v.isAlwaysDataSellingVendor) {
      return vendorToCheckboxOption(v, {
        value: true,
        disabled: true,
        title: "Selling to this data recipient requires manual transmission of data.",
      });
    }
    return vendorToCheckboxOption(v);
  });

  return {
    type: "checkboxes",
    slug: "vendor-upload-data-selection",
    question: `Which third parties do you manually send data to?`,
    helpText: (
      <>
        Do not select any third parties where data is collected only through automated means on your
        website, such as via a tracker, cookie, or other systematic method.
      </>
    ),
    emptyText: "There are no vendors available.",
    options: uniqBy(uploadVendorOptionsOptions, (v) => v.key),
  };
};

const sellingQuestions = (
  answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
): SurveyQuestionProps[] => {
  return [
    sellingInstructions(), // "selling-definition-acknowledgement"
    googleAnalyticsQuestion(), // "google-analytics-data-sharing"
    shopifySNIQuestion(), // "shopify-sni"
    sellingDisclosureQuestion(answers, vendors), // "disclosure-for-selling"
    sellingVendorsQuestion(vendors), // "vendor-selling-selection",
    sellingUploadDataQuestion(), // "selling-upload-data"
    sellingUploadVendorsSelectionQuestion(vendors), // "vendor-upload-data-selection"
  ];
};

const overrideDisclosureForSellingQuestion = (
  answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
): boolean => {
  const showGAQuestion = Boolean(findGoogleAnalytics(vendors));
  const showShopifyQuestion = Boolean(findShopifyAudiences(vendors));
  const hasAlwaysSellingVendors = findAlwaysSellingVendors(vendors).length > 0;

  const gaDisclosure = showGAQuestion && answers["google-analytics-data-sharing"] == "false";
  const shopifyDisclosure = showShopifyQuestion;

  return gaDisclosure || shopifyDisclosure || hasAlwaysSellingVendors;
};

const overrideSellingUploadDataQuestion = (vendors: OrganizationDataRecipientDto[]): boolean => {
  const showShopifyQuestion = Boolean(findShopifyAudiences(vendors));
  const hasAlwaysSellingVendors = findAlwaysSellingVendors(vendors).length > 0;

  const shopifyDisclosure = showShopifyQuestion;

  return shopifyDisclosure || hasAlwaysSellingVendors;
};

const sellingVisibility = (
  answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
): Record<string, boolean> => {
  const showGAQuestion = Boolean(findGoogleAnalytics(vendors));
  const showShopifyQuestion = Boolean(findShopify(vendors));
  const onlyGaSelected = onlyGaSelectedForSelling(answers, vendors);
  const dataSellingDefinitionAcknowledged = Boolean(answers["selling-definition-acknowledgement"]);

  const disclosureForSellingAnswered =
    overrideDisclosureForSellingQuestion(answers, vendors) ||
    answers["disclosure-for-selling"] == "true";
  const sellingUploadDataAnswered =
    overrideSellingUploadDataQuestion(vendors) || answers["selling-upload-data"] == "true";

  return {
    "disclosure-for-selling": dataSellingDefinitionAcknowledged,
    "vendor-selling-selection": dataSellingDefinitionAcknowledged && disclosureForSellingAnswered,
    "google-analytics-data-sharing": dataSellingDefinitionAcknowledged && showGAQuestion,
    "shopify-sni": dataSellingDefinitionAcknowledged && showShopifyQuestion,
    "selling-upload-data":
      dataSellingDefinitionAcknowledged && disclosureForSellingAnswered && !onlyGaSelected,
    "vendor-upload-data-selection":
      dataSellingDefinitionAcknowledged &&
      disclosureForSellingAnswered &&
      sellingUploadDataAnswered &&
      !onlyGaSelected,
  };
};

export interface SellingSurveyFlags {
  hadShopifySNIBeforeSurvey?: boolean;
}

// Returns true, false, or reason for true
/**
 * Whether selling questions should be disabled based on answers and vendors.
 * @param answers Current survey answers
 * @param vendors Current list of vendors
 * @param flags Indicators about the page state of the survey to help decide whether to lock down questions or not. This
 *              is mostly to drive "grace periods" where a user can change their answer to a survey question in spite of
 *              something on the backend changing that would normally result in the survey question becoming locked. For
 *              example, answering "No" to the Shopify SNI question creates the Shopify SNI vendor immediately. However,
 *              the survey question is disabled if the SNI vendor exists. We want to allow the user to change their mind
 *              and switch the answer to "Yes" if they *just* clicked "No" (even though we made the vendor already we'll
 *              clean it up on the backend when the answer changes). Otherwise, keep the question locked down.
 */
const sellingDisabled = (
  answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
  flags: SellingSurveyFlags = {},
): Record<string, boolean | string> => {
  return {
    "disclosure-for-selling":
      overrideDisclosureForSellingQuestion(answers, vendors) &&
      "‘Yes’ is selected here based on your answers to the previous questions.",
    "selling-upload-data":
      overrideSellingUploadDataQuestion(vendors) &&
      "‘Yes’ is selected here based on your answers to the previous questions.",
    "shopify-sni":
      Boolean(findShopifyAudiences(vendors)) &&
      flags.hadShopifySNIBeforeSurvey &&
      "You already added Shopify Network Intelligence as a vendor. To change your answer, return to Vendors and remove it.",
  };
};

export const sellingSurvey = (
  answers: SurveyAnswers,
  vendors: OrganizationDataRecipientDto[],
  flags: SellingSurveyFlags = {},
): SurveyDefinition => {
  return {
    questions: sellingQuestions(answers, vendors),
    visibility: sellingVisibility(answers, vendors),
    disabled: sellingDisabled(answers, vendors, flags),
    relatedQuestionGroups: [[SHOPIFY_SNI_SLUG]],
  };
};
